import React, {useCallback, useEffect, useState} from "react";
import style from "../../../assets/scss/common/modal/addChildModal.module.scss";
import CloseBtn from "../../../assets/images/modal/close_btn.svg";
import {useAppDispatch, useModal} from "../../../hooks/hooks";
import useValidation from "../../../hooks/useValidation";
import {createProfileAsync} from "../../../store/profile/profileSlice";
import {
  Address,
  CountryCode,
  CountryFilterValue,
  CountryName,
  InitialProfileInfoDTO,
  NewUserProfile,
  ProfileErrorDTO
} from "../../../types/user";
import {ChildNameField} from "./mypage/ChildNameField";
import {ChildAgeField} from "./mypage/ChildAgeField";
import {ChildGenderField} from "./mypage/ChildGenderField";
import {countryCodeMap} from "../../../util/commonFunctions";
import {ChildAddressField} from "./mypage/ChildAddressField";
import {useTranslation} from "react-i18next";
import {ProfileCommonDescription} from "./mypage/ProfileCommonDescription";

interface AddChildModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProfileAdded: () => void; // 프로필 추가 콜백
  parentAddress?: Address; // New prop to receive the parent's address
}

const AddChildModal: React.FC<AddChildModalProps> = ({ isOpen, onClose, onProfileAdded, parentAddress }) => {
  const { t } = useTranslation("profileInfo");
  const dispatch = useAppDispatch();
  const [isChecked, setIsChecked] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<CountryFilterValue>(CountryFilterValue.NONE);

  const initialProfileInfo = {
    name: "",
    birthday: "",
    gender: "",
    address1: "",
    address2: "",
    address3: "",
    city: "",
    state: "",
    country: "",
    postalCode: "",
  };

  const [profileInfo, setProfileInfo] = useState<InitialProfileInfoDTO>(initialProfileInfo);
  const { validateName, validateBirthday, validateGender } =
    useValidation();
  const [errors, setErrors] = useState<ProfileErrorDTO>({
    name: "",
    birthday: "",
    gender: "",
  });

  const { openConfirmModal, openAlertModal } = useModal();

  const getCountryCode = (selectedCountry: CountryName): CountryCode => {
    return countryCodeMap[selectedCountry];
  };

  const validateProfileInfo = (profile: InitialProfileInfoDTO) => {
    const validationErrors = {
      name: validateName(profile.name),
      birthday: validateBirthday(profile.birthday),
      gender: validateGender(profile.gender),
    };
    return validationErrors;
  };

  const handleSubmit = () => {
    const validationErrors = validateProfileInfo(profileInfo);
    setErrors(validationErrors);

    if (Object.values(validationErrors).some((error) => error)) {
      return;
    }

    createProfile({
      name: profileInfo.name,
      birthday: `${profileInfo.birthday.slice(0, 4)}-${profileInfo.birthday.slice(4, 6)}-${profileInfo.birthday.slice(
        6
      )}`,
      gender: profileInfo.gender,
      address: {
        address1: profileInfo.address1,
        address2: profileInfo.address2,
        address3: profileInfo.address3,
        city: profileInfo.city,
        state: profileInfo.state,
        country: getCountryCode(selectedCountry),
        postalCode: profileInfo.postalCode,
      },
    })
      .then(onProfileAdded)
      .then(onClose);

    openAlertModal({
      title: t('profileInfo.modal.add.title'),
      desc: t('profileInfo.modal.add.complete'),
    });
  };

  const createProfile = useCallback(
    async (profileInfo: NewUserProfile) => {
      try {
        const response = await dispatch(createProfileAsync(profileInfo)).unwrap();
        console.log("create profile!", response, profileInfo);
      } catch (error) {
        console.log(error);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    if (!isOpen) {
      setProfileInfo(initialProfileInfo);
      setErrors({
        name: "",
        birthday: "",
        gender: "",
      });
      setSelectedCountry(CountryFilterValue.NONE);
      setIsChecked(false);
    }
    // eslint-disable-next-line
  }, [isOpen, parentAddress]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className={style.addChildModalOverlay}>
      <div className={style.addChildInfoModal}>
        <div className={style.closeBtnContainer}>
          <img
            src={CloseBtn}
            alt="closeBtn"
            className={style.closeBtn}
            onClick={() => {
              openConfirmModal(
                {
                  title: t('profileInfo.modal.add.title'),
                  desc: `${t('profileInfo.modal.add.desc1')}\n${t('profileInfo.modal.add.desc2')}`,
                },
                () => {
                  onClose();
                }
              );
            }}
          />
        </div>
        <div className={style.innerModalContainer}>
          <h3 className={style.addChildInfoTitle}>{t('profileInfo.addModal.title')}</h3>
          <ProfileCommonDescription />
          <div className={style.innerTextContainer}>
            <ChildNameField profileInfo={profileInfo} setProfileInfo={setProfileInfo} errors={errors}/>

            <ChildAgeField profileInfo={profileInfo} setProfileInfo={setProfileInfo} errors={errors}/>

            <ChildGenderField
                profileInfo={profileInfo} setProfileInfo={setProfileInfo}
                errors={errors} setErrors={setErrors}/>

            <ChildAddressField profileInfo={profileInfo} setProfileInfo={setProfileInfo}
                               parentAddress={parentAddress}
                               selectedCountry={selectedCountry} setSelectedCountry={setSelectedCountry}
                               isChecked={isChecked} setIsChecked={setIsChecked}
            />

          </div>
          <button className={style.submitBtn} onClick={handleSubmit}>
            {t('profileInfo.btn.register')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddChildModal;
